import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * SignalAnalyticEngine - High-resolution Morse input analyzer
 * 
 * Provides real-time metrics for:
 * - Element Weighting (Dash:Dit ratio)
 * - Timing Jitter (Standard deviation of elements)
 * - Iambic Efficiency (Physical movements per character)
 * - Squeeze Detection (Simultaneous closure)
 */
export const useSignalAnalyticEngine = (options = {}) => {
    const {
        enabled = true,
        wpm = 20,
        farnsworthWpm = 20,
        onDiagnosis = null
    } = options;

    const [metrics, setMetrics] = useState({
        weighting: 3.0,
        jitter: 0,
        efficiency: 0,
        squeezeRatio: 0,
        avgDitMs: 0,
        avgDashMs: 0,
        totalElements: 0,
        totalMoves: 0,
        errorRate: 0,
        isSqueezing: false
    });

    // Internal state refs for high-speed tracking
    const state = useRef({
        left: false,
        right: false,
        history: [], // Recent Make/Break events
        elementBuffer: [], // List of completed elements with their lengths
        currentElementStart: null,
        currentElementType: null,
        lastBreakTime: null,
        totalMoves: 0,
        totalChars: 0
    });

    const calculateMetrics = useCallback(() => {
        const history = state.current.history;
        const squeezeRatio = history.length >= 4
            ? (() => {
                const recentMakes = history.filter(h => h.type === 'make').slice(-20);
                const squeezes = recentMakes.filter(h => h.squeezed).length;
                return recentMakes.length > 0 ? (squeezes / recentMakes.length) * 100 : 0;
            })()
            : 0;

        // 2. Weighting & Jitter
        const dits = state.current.elementBuffer.filter(e => e.type === 'dot');
        const dashes = state.current.elementBuffer.filter(e => e.type === 'dash');

        const avgDit = dits.length > 0 ? dits.reduce((a, b) => a + b.duration, 0) / dits.length : 0;
        const avgDash = dashes.length > 0 ? dashes.reduce((a, b) => a + b.duration, 0) / dashes.length : 0;
        const weighting = avgDit > 0 && avgDash > 0 ? avgDash / avgDit : 3.0;

        // Dynamic Jitter (Std Dev of last 20 dits)
        let jitter = 0;
        if (dits.length >= 3) {
            const recentDits = dits.slice(-20);
            const mean = recentDits.reduce((a, b) => a + b.duration, 0) / recentDits.length;
            const variance = recentDits.reduce((a, b) => a + Math.pow(b.duration - mean, 2), 0) / recentDits.length;
            jitter = Math.sqrt(variance);
        }

        setMetrics(prev => ({
            ...prev,
            squeezeRatio,
            jitter,
            isSqueezing: state.current.left && state.current.right,
            totalMoves: state.current.totalMoves,
            avgDitMs: avgDit.toFixed(0),
            avgDashMs: avgDash.toFixed(0),
            weighting: weighting.toFixed(2)
        }));
    }, []);

    const recordMove = useCallback((paddle, isDown) => {
        if (!enabled) return;

        const now = performance.now();
        const prevLeft = state.current.left;
        const prevRight = state.current.right;

        // Update state
        if (paddle === 'left') state.current.left = isDown;
        if (paddle === 'right') state.current.right = isDown;

        const isSqueezing = state.current.left && state.current.right;

        // Transition detected?
        if (prevLeft !== state.current.left || prevRight !== state.current.right) {
            state.current.totalMoves++;

            const event = {
                type: isDown ? 'make' : 'break',
                paddle,
                time: now,
                squeezed: isSqueezing
            };
            state.current.history.push(event);
            if (state.current.history.length > 200) state.current.history.shift();

            // Calculate metrics on every transition
            calculateMetrics();
        }
    }, [enabled, calculateMetrics]);

    const recordElement = useCallback((type, duration) => {
        if (!enabled) return;
        state.current.elementBuffer.push({ type, duration, time: performance.now() });
        if (state.current.elementBuffer.length > 100) state.current.elementBuffer.shift();
        calculateMetrics();
    }, [enabled, calculateMetrics]);

    // Diagnostic reporter (runs less frequently or on end of session)
    const runDiagnosis = useCallback(() => {
        const dits = state.current.elementBuffer.filter(e => e.type === 'dot');
        const dashes = state.current.elementBuffer.filter(e => e.type === 'dash');

        if (dits.length === 0) return null;

        const avgDit = dits.reduce((a, b) => a + b.duration, 0) / dits.length;
        const avgDash = dashes.length > 0 ? dashes.reduce((a, b) => a + b.duration, 0) / dashes.length : 0;
        const weighting = avgDash > 0 ? avgDash / avgDit : 3.0;

        // Calculate jitter (std dev of dits)
        const jitter = Math.sqrt(dits.map(x => Math.pow(x.duration - avgDit, 2)).reduce((a, b) => a + b) / dits.length);

        const report = {
            weighting: weighting.toFixed(2),
            jitter: jitter.toFixed(1),
            avgDit: avgDit.toFixed(0),
            totalElements: state.current.elementBuffer.length,
            moves: state.current.totalMoves
        };

        if (onDiagnosis) onDiagnosis(report);
        return report;
    }, [onDiagnosis]);

    return {
        metrics,
        recordMove,
        recordElement,
        runDiagnosis,
        reset: () => {
            state.current = {
                left: false,
                right: false,
                history: [],
                elementBuffer: [],
                currentElementStart: null,
                currentElementType: null,
                lastBreakTime: null,
                totalMoves: 0,
                totalChars: 0
            };
            setMetrics({
                weighting: 3.0,
                jitter: 0,
                efficiency: 0,
                squeezeRatio: 0,
                avgDitMs: 0,
                avgDashMs: 0,
                totalElements: 0,
                totalMoves: 0,
                errorRate: 0,
                isSqueezing: false
            });
        }
    };
};
